import React from 'react';
import {
  Container,
  Typography,
  Box,
  Paper,
  Grid,
  Card,
  CardContent,
  List,
  ListItem,
  ListItemIcon,
  ListItemText,
  Divider,
  Button
} from '@mui/material';
import {
  Timeline as TimelineIcon,
  CheckCircle as CheckCircleIcon,
  ArrowBack as ArrowBackIcon,
  AccountTree as AccountTreeIcon,
  Visibility as VisibilityIcon,
  TrendingUp as TrendingUpIcon,
  Security as SecurityIcon
} from '@mui/icons-material';
import { useNavigate } from 'react-router-dom';

const DataLineageFeature: React.FC = () => {
  const navigate = useNavigate();

  const benefits = [
    'Visual tracking of data flow from source to consumption',
    'Transparency into data transformations and dependencies',
    'Impact analysis for proposed changes',
    'Root cause analysis for data quality issues',
    'Compliance and audit trail documentation',
    'Better understanding of data provenance'
  ];

  const capabilities = [
    {
      icon: <AccountTreeIcon sx={{ color: 'primary.main' }} />,
      title: 'Visual Lineage',
      description: 'Interactive diagrams showing data flow and transformations'
    },
    {
      icon: <VisibilityIcon sx={{ color: 'primary.main' }} />,
      title: 'End-to-End Tracking',
      description: 'Track data from original source through all transformations to final destination'
    },
    {
      icon: <TrendingUpIcon sx={{ color: 'primary.main' }} />,
      title: 'Impact Analysis',
      description: 'Assess downstream effects of data changes before implementation'
    },
    {
      icon: <SecurityIcon sx={{ color: 'primary.main' }} />,
      title: 'Compliance Support',
      description: 'Document data flows for regulatory compliance and audits'
    }
  ];

  return (
    <Container maxWidth="lg" sx={{ py: 4 }}>
      <Button
        startIcon={<ArrowBackIcon />}
        onClick={() => navigate(-1)}
        sx={{ mb: 3, color: 'primary.main' }}
      >
        Back
      </Button>

      <Box sx={{ mb: 4 }}>
        <Box sx={{ display: 'flex', alignItems: 'center', mb: 2 }}>
          <TimelineIcon sx={{ fontSize: 48, color: 'primary.main', mr: 2 }} />
          <Typography variant="h3" sx={{ color: 'primary.main', fontWeight: 'bold' }}>
            Data Lineage
          </Typography>
        </Box>
        <Typography variant="h6" color="text.secondary" sx={{ mb: 3 }}>
          Visual tracking of data flow from source to consumption
        </Typography>
        
        {/* Access Application Button */}
        <Button
          variant="contained"
          size="large"
          onClick={() => navigate('/data-lineage')}
          sx={{
            bgcolor: 'primary.main',
            color: 'white',
            px: 4,
            py: 1.5,
            fontSize: '1.1rem',
            fontWeight: 600,
            textTransform: 'none',
            '&:hover': {
              bgcolor: 'primary.dark'
            }
          }}
        >
          Access Data Lineage Application →
        </Button>
      </Box>

      <Paper sx={{ p: 4, mb: 4, borderLeft: '4px solid #003366' }}>
        <Typography variant="h5" gutterBottom sx={{ color: 'primary.main', fontWeight: 600 }}>
          Overview
        </Typography>
        <Typography paragraph>
          Data Lineage provides complete visibility into how data moves through your systems, from its original source through all transformations to its final destination. This transparency is essential for understanding data quality, performing impact analysis, and maintaining compliance.
        </Typography>
        <Typography paragraph>
          By visualizing data flows and dependencies, teams can quickly identify the root cause of data issues, assess the impact of proposed changes, and ensure data integrity across the enterprise.
        </Typography>
      </Paper>

      <Paper sx={{ p: 4, mb: 4, bgcolor: '#f8f9fa' }}>
        <Typography variant="h5" gutterBottom sx={{ color: 'primary.main', fontWeight: 600 }}>
          Key Benefits
        </Typography>
        <List>
          {benefits.map((benefit, index) => (
            <ListItem key={index}>
              <ListItemIcon>
                <CheckCircleIcon sx={{ color: 'primary.main' }} />
              </ListItemIcon>
              <ListItemText primary={benefit} />
            </ListItem>
          ))}
        </List>
      </Paper>

      <Typography variant="h5" gutterBottom sx={{ color: 'primary.main', fontWeight: 600, mb: 3 }}>
        Core Capabilities
      </Typography>
      <Grid container spacing={3} sx={{ mb: 4 }}>
        {capabilities.map((capability, index) => (
          <Grid item xs={12} md={6} key={index}>
            <Card sx={{ height: '100%', borderLeft: '4px solid #003366' }}>
              <CardContent>
                <Box sx={{ display: 'flex', alignItems: 'center', mb: 2 }}>
                  {capability.icon}
                  <Typography variant="h6" sx={{ ml: 1, color: 'primary.main', fontWeight: 600 }}>
                    {capability.title}
                  </Typography>
                </Box>
                <Typography variant="body2" color="text.secondary">
                  {capability.description}
                </Typography>
              </CardContent>
            </Card>
          </Grid>
        ))}
      </Grid>

      <Paper sx={{ p: 4, mb: 4 }}>
        <Typography variant="h5" gutterBottom sx={{ color: 'primary.main', fontWeight: 600 }}>
          Common Use Cases
        </Typography>
        <Divider sx={{ mb: 3 }} />
        
        <Box sx={{ mb: 3 }}>
          <Typography variant="h6" gutterBottom sx={{ color: 'primary.main' }}>
            Root Cause Analysis
          </Typography>
          <Typography paragraph>
            Quickly trace data quality issues back to their source by following the lineage upstream.
          </Typography>
        </Box>

        <Box sx={{ mb: 3 }}>
          <Typography variant="h6" gutterBottom sx={{ color: 'primary.main' }}>
            Change Impact Assessment
          </Typography>
          <Typography paragraph>
            Understand which downstream systems and reports will be affected by proposed data changes.
          </Typography>
        </Box>

        <Box>
          <Typography variant="h6" gutterBottom sx={{ color: 'primary.main' }}>
            Regulatory Compliance
          </Typography>
          <Typography paragraph>
            Document data flows and transformations for audit trails and compliance reporting.
          </Typography>
        </Box>
      </Paper>

      <Paper sx={{ p: 4, bgcolor: 'primary.main', color: 'white', textAlign: 'center' }}>
        <Typography variant="h5" gutterBottom sx={{ fontWeight: 600 }}>
          Ready to Track Your Data Flows?
        </Typography>
        <Typography variant="body1" sx={{ mb: 3 }}>
          Learn more about how E-Unify's Data Lineage can help your organization
        </Typography>
        <Button
          variant="contained"
          size="large"
          onClick={() => navigate('/contact')}
          sx={{
            bgcolor: 'white',
            color: 'primary.main',
            '&:hover': { bgcolor: '#f8f9fa' }
          }}
        >
          Contact Us
        </Button>
      </Paper>
    </Container>
  );
};

export default DataLineageFeature;
